import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import { STATES, REGIONS } from "@/data/states";

export default function StateHub() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title="Senior care by state — costs, options & local guidance"
        description="Find senior care options in every US state. Average costs, popular cities, Medicaid waivers, and a free 3-minute quiz to match you with the right level of care near you."
        path="/senior-care"
        keywords="senior care by state, assisted living near me, memory care by state, senior living finder"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Senior care by state",
          "url": "https://carehomeconnect.org/senior-care",
          "description": "Senior care guidance and cost data for every US state.",
          "hasPart": STATES.map((s) => ({
            "@type": "WebPage",
            "name": `Senior care in ${s.name}`,
            "url": `https://carehomeconnect.org/senior-care/${s.slug}`,
          })),
        }}
      />
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10">
        <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">By state</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A2E24] tracking-tight max-w-3xl">
          Senior care, wherever your family is
        </h1>
        <p className="mt-5 text-lg text-[#5C6B62] max-w-2xl leading-relaxed">
          Average costs, top metros, and what to know about Medicaid in your state — plus a free 3-minute quiz that matches you with the right level of care near you.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {REGIONS.map((region) => {
          const inRegion = STATES.filter((s) => s.region === region);
          return (
            <div key={region} className="mb-12" data-testid={`states-region-${region.toLowerCase()}`}>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mb-6">{region}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {inRegion.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/senior-care/${s.slug}`}
                    data-testid={`state-link-${s.slug}`}
                    className="card-soft p-5 group flex items-center justify-between hover:bg-white"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center text-[#2C4C3B] text-xs font-semibold">
                        {s.abbr}
                      </div>
                      <div>
                        <div className="font-display font-semibold text-[#1A2E24]">{s.name}</div>
                        <div className="text-xs text-[#5C6B62]">From ${(s.cost.independent).toLocaleString()}/mo</div>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#9CAF88] group-hover:text-[#2C4C3B] group-hover:translate-x-1 transition" />
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="bg-[#2C4C3B] rounded-3xl p-10 md:p-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-medium mb-5">
            <MapPin className="w-3.5 h-3.5" /> Local matches in every state
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white max-w-2xl mx-auto leading-tight">
            Not sure which level of care fits?
          </h2>
          <p className="mt-5 text-[#F7F5F0]/80 max-w-lg mx-auto">
            Take our free 3-minute quiz. We'll recommend the right care type and match you with vetted communities in your zip code.
          </p>
          <Link to="/quiz" data-testid="state-hub-quiz-cta" className="inline-block mt-8">
            <span className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-14 px-8 text-base font-medium">
              Find care near me <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
